import type { SupabaseClient } from "@supabase/supabase-js";
import type { InvestorDatabasePayload, InvestorDatabaseRow } from "../types";

export interface DataClient {
  mode: "local" | "supabase";
  label: string;
  loadRecords(): Promise<InvestorDatabaseRow[]>;
  updateRecord(id: string, payload: InvestorDatabasePayload): Promise<void>;
  createRecord(payload: InvestorDatabasePayload): Promise<void>;
  deleteRecord(id: string): Promise<void>;
}

export function createSupabaseDataClient(client: SupabaseClient): DataClient {
  return {
    mode: "supabase",
    label: "Supabase",
    async loadRecords() {
      const { data, error } = await client
        .from("investors")
        .select("*")
        .order("updated_at", { ascending: false });
      if (error) throw error;
      return (data || []) as InvestorDatabaseRow[];
    },
    async updateRecord(id, payload) {
      const { error, count } = await client
        .from("investors")
        .update(payload, { count: "exact" })
        .eq("id", id);
      if (error) throw error;
      if (count === 0) throw new Error(`No row updated for id ${id}.`);
    },
    async createRecord(payload) {
      const { error } = await client.from("investors").insert(payload);
      if (error) throw error;
    },
    async deleteRecord(id) {
      const { error, count } = await client
        .from("investors")
        .delete({ count: "exact" })
        .eq("id", id);
      if (error) throw error;
      if (count === 0) throw new Error(`No row deleted for id ${id}.`);
    },
  };
}

export function createLocalDataClient(baseUrl: string): DataClient {
  const endpoint = `${baseUrl.replace(/\/+$/, "")}/investors`;
  const jsonHeaders = {
    "Content-Type": "application/json",
    Prefer: "return=minimal",
  };
  const countHeaders = {
    "Content-Type": "application/json",
    Prefer: "return=minimal,count=exact",
  };

  return {
    mode: "local",
    label: "Docker DB",
    async loadRecords() {
      return requestLocalJson<InvestorDatabaseRow[]>(`${endpoint}?select=*&order=updated_at.desc`);
    },
    async updateRecord(id, payload) {
      const response = await fetchLocal(`${endpoint}?id=eq.${encodeURIComponent(id)}`, {
        method: "PATCH",
        headers: countHeaders,
        body: JSON.stringify(payload),
      });
      if (affectedRows(response) === 0) throw new Error(`No row updated for id ${id}.`);
    },
    async createRecord(payload) {
      await requestLocalJson(endpoint, {
        method: "POST",
        headers: jsonHeaders,
        body: JSON.stringify(payload),
      });
    },
    async deleteRecord(id) {
      const response = await fetchLocal(`${endpoint}?id=eq.${encodeURIComponent(id)}`, {
        method: "DELETE",
        headers: { Prefer: "return=minimal,count=exact" },
      });
      if (affectedRows(response) === 0) throw new Error(`No row deleted for id ${id}.`);
    },
  };
}

function affectedRows(response: Response): number | null {
  const range = response.headers.get("Content-Range");
  if (!range) return null;
  const match = range.match(/\/(\d+|\*)$/);
  if (!match || match[1] === "*") return null;
  return Number(match[1]);
}

async function fetchLocal(url: string, options: RequestInit): Promise<Response> {
  const response = await fetch(url, options);
  if (!response.ok) {
    const message = await response.text().catch(() => "");
    throw new Error(message || `${response.status} ${response.statusText}`);
  }
  return response;
}

async function requestLocalJson<T = unknown>(url: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(url, options);
  if (!response.ok) {
    const message = await response.text().catch(() => "");
    throw new Error(message || `${response.status} ${response.statusText}`);
  }
  if (response.status === 204) return null as T;
  const text = await response.text();
  return text ? (JSON.parse(text) as T) : (null as T);
}
